/* eslint-disable @typescript-eslint/no-explicit-any -- disable */
/* eslint-disable @typescript-eslint/no-unsafe-member-access -- disable unsafe member for this file */
/* eslint-disable @typescript-eslint/no-unsafe-assignment -- disable unsafe member for this file */
import type { AxiosError, AxiosResponse, RawAxiosRequestHeaders } from "axios";
import { stringify } from "qs";
import { HttpClient } from "./utils/http-client";

const CURRENT_BASE_URL = "https://dash.khazartransfo.com/api";

export interface ErrorType {
  message: string | null;
  status: number | null;
}

export interface PaginationType {
  total: number;
  count: number;
  per_page: number;
  current_page: number;
  total_pages: number;
}

export interface SeoType {
  data: {
    id: number;
    title: string | null;
    description: string | null;
    author: string | null;
    robots: string | null;
  };
}
export interface BreadCrumb {
  label: string;
  url: string;
}
export interface InterfaceBaseRepository<T, U = T, C = T, R = T[]> {
  get: (
    id: number,
    queryParams?: Record<string, unknown>,
    header?: RawAxiosRequestHeaders
  ) => Promise<ApiResponse<T>>;
  getMany: (queryParams?: Record<string, unknown>) => Promise<ApiResponse<R>>;
  create: (
    item: C | FormData,
    header?: RawAxiosRequestHeaders
  ) => Promise<ApiResponse<T>>;
  update: (
    id: number | string,
    item: U,
    header?: RawAxiosRequestHeaders
  ) => Promise<ApiResponse<T>>;
  delete: (id: number) => Promise<ApiResponse<T>>;
}

export interface ApiResponse<T> {
  data: T | null;
  seo: SeoType | null;
  succeeded: boolean | null;
  errors: AxiosError | null;
  pagination: PaginationType | null;
  breadCrumbs: BreadCrumb[] | null;
}

const transform = <R>(
  response: AxiosResponse | AxiosError
): Promise<ApiResponse<R>> => {
  if ((response as AxiosError).isAxiosError) {
    // Handle AxiosError
    const error = response as AxiosError;
    return Promise.resolve({
      data: null,
      seo: null,
      succeeded: false,
      errors: error,
      pagination: null,
      breadCrumbs: null,
    });
  }
  // Handle successful AxiosResponse
  const successResponse = response as AxiosResponse;

  return Promise.resolve({
    // eslint-disable-next-line no-nested-ternary -- disable nested ternary
    data: successResponse.data
      ? "data" in successResponse.data
        ? successResponse.data.data
        : successResponse.data
      : null,
    seo: successResponse.data.data?.seo || null,
    succeeded: successResponse.status >= 200 && successResponse.status < 300,
    errors: null,
    pagination: successResponse.data.meta?.pagination || null,
    breadCrumbs: successResponse.data?.breadcrumbs || null,
  });
};

export abstract class BaseRepository<T, U = T, C = T, R = T[]>
  extends HttpClient
  implements InterfaceBaseRepository<T, U, C, R>
{
  protected collection: string | undefined;
  protected language?: string | undefined;
  // constructor(collection: string, language?: string) {
  //   super();
  //   this.collection = collection;
  //   this.language = language;
  // }

  public async get(
    id: number | string,
    queryParams?: Record<string, unknown>,
    header?: RawAxiosRequestHeaders
  ): Promise<ApiResponse<T>> {
    const instance = this.getInstance();
    const result = await instance
      .get(`${CURRENT_BASE_URL}/${this.collection}/${id}`, {
        params: queryParams,
        paramsSerializer: (params) => {
          return stringify(params, { arrayFormat: "repeat" });
        },
        headers: {
          "Accept-Language": this.language || "en",
          ...header,
        },
      })
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async getMany(
    queryParams?: Record<string, unknown>
  ): Promise<ApiResponse<R>> {
    const instance = this.getInstance();
    const result = await instance
      .get(`${CURRENT_BASE_URL}/${this.collection}`, {
        params: queryParams,
        paramsSerializer: (params) => {
          return stringify(params, { arrayFormat: "repeat" });
        },
        headers: {
          "Accept-Language": this.language || "en",
        },
      })
      .then(transform);
    return result as ApiResponse<R>;
  }

  public async create(
    item: C | FormData,
    headers?: RawAxiosRequestHeaders
  ): Promise<ApiResponse<T>> {
    const instance = this.getInstance();
    const result = await instance
      .post(`${CURRENT_BASE_URL}/${this.collection}/`, item, {
        headers: { "Content-Type": "multipart/form-data", ...headers },
      })
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async update(
    id: number | string,
    item: U,
    header?: RawAxiosRequestHeaders
  ): Promise<ApiResponse<T>> {
    const instance = this.getInstance();
    const result = await instance
      .post(`${CURRENT_BASE_URL}/${this.collection}/${id}`, item, {
        params: { _method: "patch" },
        headers: { ...header },
      })
      .then(transform);
    return result as ApiResponse<T>;
  }

  public async getNested<NestedType = R>(
    path: string,
    queryParams?: Record<string, unknown>,
    headers?: RawAxiosRequestHeaders
  ): Promise<ApiResponse<NestedType>> {
    const instance = this.getInstance();

    const result = await instance
      .get(`${CURRENT_BASE_URL}/${path}`, {
        params: queryParams,
        paramsSerializer: (params) =>
          stringify(params, { arrayFormat: "repeat" }),
        headers: {
          "Accept-Language": this.language || "en",
          ...headers,
        },
      })
      .then(transform);

    return result as ApiResponse<NestedType>;
  }

  public async delete(id: number): Promise<ApiResponse<T>> {
    const instance = this.getInstance();
    const result = await instance
      .delete(`${CURRENT_BASE_URL}/${this.collection}/${id}`)
      .then(transform);
    return result as ApiResponse<T>;
  }
}
